// CRM 2.0 booking card on the portal Home (M3-F2; rows M3.16, M3.24, M3.25, M3.26, M3.40;
// DC-74 / DC-76 / DC-95 / DC-113 / DC-146). Replaces today's stepper + card for CRM 2.0 bookings only;
// old bookings keep Home's Token → Allotment → Balance SDR → Balance OCR & GST → Agreement.
//   ribbon "Stage now: …" (never "CRM 2.0") · stepper with Done / In progress / Next ·
//   header (unit, project, one state chip, ⋮) · one next-step box + the stage's steps ·
//   folded unit details · help line · Back / Next to look at earlier stages.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AllotmentDetails from "../../AllotmentDetails/AllotmentDetails";
import UnitCost from "../../../Modals/UnitCost/UnitCost";
import AllotmentBuildingIcon from "../../../Assets/Images/Common/AllotmentBuildingIcon.svg";
import useCrm2Card from "./useCrm2Card";
import usePayNow from "./usePayNow";
import Crm2Stepper from "./Crm2Stepper";
import ShortlistedSteps from "./ShortlistedSteps";
import AfbStage from "./stages/AfbStage";
import { AllotmentLetterStage, FinalDraftStage, RegistrationStage, CompleteStage } from "./stages/LaterStages";
import { Btn, NextBox, niceDate, inr } from "./Crm2Parts";
import useConsentPoint from "./TermsGate";
import { crm2CardModals } from "./crm2CardModals";
import css from "./Crm2Booking.module.css";
import { completeLetterEsign } from "../../../Services/Crm2Portal";
import { errorTost, successTost } from "../../../Services/Toast";

const STATE = (card) => (card.stage === "CANCELLED" ? "Cancelled" : card.stage === 0 ? "Shortlisted" : card.stage < 3 ? "Applied" : "Allotted");
const STATE_CHIP = { Shortlisted: css.cG, Applied: css.cInd, Allotted: css.cOk, Cancelled: css.cEr };

function ribbonText(card) {
	if (card.stage === "CANCELLED") return ["Cancelled", "Cancelled at your request or VJ's"];
	if (card.stage === 0) return ["Shortlisted", card.money?.paid > 0 ? "Pay the balance booking amount" : "Unit shortlisted · pay booking amount"];
	if (card.bookingState === "WAITING_RM" && card.stage === 1) return ["Application for Booking · not started", "Booking amount paid · waiting for your Relationship Manager"];
	if (card.stage === 1) return ["Application for Booking", card.afb?.confirmedAt || card.kycFilled ? "Booking amount paid · Application for Booking steps" : "Booking amount paid · confirm your Application for Booking"];
	return [card.stageName, ""];
}

function PayTimer({ expiry }) {
	const [left, setLeft] = useState(Math.max(expiry - Date.now(), 0));
	useEffect(() => {
		const id = setInterval(() => setLeft(Math.max(expiry - Date.now(), 0)), 1000);
		return () => clearInterval(id);
	}, [expiry]);
	const s = Math.floor(left / 1000);
	const hh = String(Math.floor(s / 3600)).padStart(2, "0");
	const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
	const ss = String(s % 60).padStart(2, "0");
	return (
		<div className={css.tmr}>
			<b>{`${hh}:${mm}:${ss}`}</b>
			<span>left to pay</span>
		</div>
	);
}

function UnitDetails({ card }) {
	const st = card.stage === 0 ? 0 : card.stage < 3 ? 1 : 2;
	const tl = [
		["Shortlisted On", card.dates?.shortlistedOn, "Unit picked for you"],
		["Applied On", card.dates?.appliedOn, "Booking amount fully paid"],
		["Allotted On", card.dates?.allottedOn, "Allotment letter eSigned"],
	];
	return (
		<div className={css.moreB}>
			<div className={css.uis}>
				<div className={css.uisH}>Status</div>
				<div className={css.dtl}>
					{tl.map(([t, d, sub], i) => (
						<div key={t} className={i <= st && d ? css.on : ""}>
							<i>{i <= st && d ? "✓" : ""}</i>
							<b>{t}</b>
							<span>{i <= st && d ? niceDate(d) : "Not yet"}</span>
							<em>{sub}</em>
						</div>
					))}
				</div>
			</div>
			<div className={css.uis}>
				<div className={css.uisH}>Payment</div>
				<div className={css.dbig}>
					<div>
						<span>Total Cost of Unit</span>
						<b>{card.money?.unitCostTotal ? inr(card.money.unitCostTotal) : "—"}</b>
					</div>
					<div>
						{/* prototype: everything paid so far (booking amount + stamp duty & charges + later payments) */}
						<span>Amount paid till date</span>
						<b>{inr(card.money?.paid)}</b>
					</div>
				</div>
			</div>
			<div className={css.uis}>
				<div className={css.uisH}>Unit</div>
				{[
					["Unit :", card.unit?.label],
					["Unit type :", card.unit?.type],
					["Floor :", card.unit?.floor],
					["Project :", card.unit?.projectName],
					["MahaRERA No. :", card.unit?.reraNo],
					// M6-F9 (DC-77): areas as on the cost sheet
					["RERA carpet area :", card.areas ? `${card.areas.reraCarpetSqFt} sq ft` : null],
					["Total carpet area :", card.areas ? `${card.areas.totalCarpetSqFt} sq ft${card.areas.childAreas?.length ? ` (with ${card.areas.childAreas.map((c) => `${c.type.toLowerCase()} ${c.sqFt}`).join(", ")})` : ""}` : null],
				]
					.filter(([, v]) => v !== null && v !== undefined && v !== "")
					.map(([k, v]) => (
						<div key={k} className={css.kv}>
							<span className={css.kvK}>{k}</span>
							<span className={css.kvV}>{v}</span>
						</div>
					))}
			</div>
			{/* M5-F7 (DC-88): Parking section in Unit Details */}
			{card.parking && (
				<div className={css.uis}>
					<div className={css.uisH}>Parking</div>
					{card.parking.skipped ? (
						<div className={css.kv}>
							<span className={css.kvV}>No parking for this unit (by your CRM)</span>
						</div>
					) : (
						card.parking.slots.map((p) => (
							<div key={p.number} className={css.kv}>
								<span className={css.kvK}>Parking {p.number} :</span>
								<span className={css.kvV}>{parkLine(p)}</span>
							</div>
						))
					)}
				</div>
			)}
		</div>
	);
}

// "Upgraded · Single Covered Parking · Podium 1 · 5.00 × 2.50 × 2.20 m" (portal prototype parkBox)
const parkLine = (p) => [p.upgraded ? "Upgraded" : "Included", p.type, p.floor, p.size].filter(Boolean).join(" · ");

// parking strip on the card once the CRM selects parking (portal prototype parkBox, DC-88)
function ParkBox({ parking }) {
	if (!parking?.slots?.length) return null;
	return parking.slots.map((p) => (
		<div key={p.number} className={css.pkb}>
			<div>
				<b>Parking {p.number}</b>
				<span>{parkLine(p)}</span>
			</div>
			<span className={`${css.chip} ${css.cOk}`}>Selected by your CRM</span>
		</div>
	));
}

export default function Crm2BookingCard({ allotment }) {
	const navigate = useNavigate();
	const { card, failed, reload } = useCrm2Card(allotment);
	const { payNow, payModals } = usePayNow(allotment);
	const { requirePoint, termsModal } = useConsentPoint();
	const [view, setView] = useState(null);
	const [menu, setMenu] = useState(false);
	const [modal, setModal] = useState(null); // { type, mode }
	const [details, setDetails] = useState(false);
	const [cost, setCost] = useState(false);
	const menuRef = useRef(null);

	// M8-F7: back from Surepass (/Home/Dashboard?crm2LetterEsign=<booking>) → check this booking's eSign once
	useEffect(() => {
		const q = new URLSearchParams(window.location.search);
		if (q.get("crm2LetterEsign") !== allotment.allotmentPaymentId) return;
		q.delete("crm2LetterEsign");
		window.history.replaceState(null, "", `${window.location.pathname}${q.toString() ? `?${q}` : ""}`);
		completeLetterEsign(allotment.allotmentPaymentId)
			.then((r) => {
				console.log("[crm2][Crm2BookingCard] eSign result", { status: r?.status });
				if (r?.status === "SIGNED") successTost("Allotment letter signed. Your unit is now Allotted.");
				else if (r?.status === "NAME_MISMATCH" || r?.status === "NAME_NOT_RECEIVED") errorTost("Signature refused: the Aadhaar name does not match the chosen applicant.");
				else if (r?.status === "PENDING") successTost("We are checking your eSign. It shows here in a few minutes.");
				else if (r?.status === "LETTER_REPLACED") errorTost("Your allotment letter was reissued. Please sign the new letter.");
				else errorTost("The eSign was not finished. You can sign again.");
				reload();
			})
			.catch((err) => errorTost(err.message || "Could not check your eSign. Please refresh in a few minutes."));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allotment.allotmentPaymentId]);

	useEffect(() => {
		if (!menu) return;
		const off = (e) => menuRef.current && !menuRef.current.contains(e.target) && setMenu(false);
		document.addEventListener("mousedown", off);
		return () => document.removeEventListener("mousedown", off);
	}, [menu]);

	if (failed && !card) {
		return (
			<div className={css.bk}>
				<div className={css.bkIn}>
					<p className={css.muted}>
						Could not load this booking. <span className={css.lk} onClick={reload}>Try again</span>
					</p>
				</div>
			</div>
		);
	}
	if (!card) return <div className={`${css.bk} ${css.loading}`} />;

	const cur = card.stage === "CANCELLED" ? 1 : card.stage;
	const shown = view === null ? cur : Math.min(view, cur);
	const [stageNow, sentence] = ribbonText(card);
	const state = STATE(card);
	const waiting = card.bookingState === "WAITING_RM" && card.stage === 1;
	const open = (type, mode) => setModal({ type, mode });
	// each M3 function registers its pop-up in crm2CardModals (Application for Booking, KYC, review draft …)
	const actions = {};
	for (const [key, def] of Object.entries(crm2CardModals)) if (def.available(card)) actions[key] = (mode) => open(def.type, typeof mode === "string" ? mode : undefined);
	actions.openVa = () => navigate("/Home/Profile");
	const ModalComp = modal && Object.values(crm2CardModals).find((d) => d.type === modal.type)?.Component;
	const menuItems = [
		["View Details", () => setDetails(true)],
		["Documents", () => navigate(`/Home/Documents?ap=${allotment.allotmentPaymentId}`)],
		[`View ${state} Unit Cost`, () => setCost(true)],
	]
		.concat(actions.openKyc && card.stage === 1 && !waiting && card.kycFilled && !card.locked ? [["Edit Agreement KYC (applicants too)", () => actions.openKyc("edit")]] : [])
		.concat(actions.openCancel ? [["Cancel Unit", () => actions.openCancel()]] : []);

	let body;
	if (card.stage === "CANCELLED") body = actions.openCancelInfo ? null : <p className={css.muted}>This booking is cancelled. See Cancel Unit Requests for the refund.</p>;
	else if (shown === 0)
		body = (
			<ShortlistedSteps
				allotment={allotment}
				info={card}
				actions={card.stage === 0 && <Btn primary onClick={payNow} disabled={!!card.expiry && card.expiry < Date.now()}>Pay Now</Btn>}
				side={card.stage === 0 && card.expiry ? <PayTimer expiry={card.expiry} /> : null}
			/>
		);
	else if (shown === 1) body = <AfbStage card={card} actions={actions} />;
	else if (shown === 2) body = <AllotmentLetterStage card={card} actions={actions} />;
	else if (shown === 3) body = <FinalDraftStage card={card} />;
	else if (shown === 4) body = <RegistrationStage card={card} allotment={allotment} />;
	else body = <CompleteStage card={card} />;

	return (
		<div className={css.bk} id={`u-${allotment.allotmentPaymentId}`}>
			<div className={css.ribbon}>
				<span>
					Stage now: <b>{stageNow}</b>
				</span>
				{sentence && <span className={css.ribbonR}>{sentence}</span>}
			</div>
			<div className={css.bkIn}>
				<Crm2Stepper stages={card.stages} cur={cur} view={shown} onView={(i) => setView(i === cur ? null : i)} allDone={card.stage === 5} />
				<div className={css.ev}>
					<div className={css.evH}>
						<div className={css.evL}>
							<span className={css.bub}>
								<img src={AllotmentBuildingIcon} alt="unit" />
							</span>
							<div>
								<div className={css.unitName}>{card.unit?.label}</div>
								<div className={css.proj}>{card.unit?.projectName}</div>
								<div className={css.sub2}>{[card.unit?.type, card.unit?.floor !== null && card.unit?.floor !== undefined && card.unit?.floor !== "" ? (/^floor/i.test(String(card.unit.floor)) ? String(card.unit.floor) : `Floor ${card.unit.floor}`) : null].filter(Boolean).join(" · ")}</div>
							</div>
						</div>
						<div className={css.ago} ref={menuRef}>
							<span className={`${css.chip} ${STATE_CHIP[state]}`}>{state}</span>
							<span className={css.dots} onClick={() => setMenu((m) => !m)} role="button" tabIndex={0}>
								⋮
								{menu && (
									<div className={css.menu}>
										{menuItems.map(([t, fn]) => (
											<div
												key={t}
												onClick={(e) => {
													e.stopPropagation();
													setMenu(false);
													fn();
												}}
											>
												{t}
											</div>
										))}
									</div>
								)}
							</span>
						</div>
					</div>
					{card.askNow && (
						<NextBox
							who="bad"
							title="Please accept the updated terms"
							actions={<Btn primary onClick={() => requirePoint("ask_now", allotment.allotmentPaymentId, () => reload())}>Read &amp; accept the updated terms</Btn>}
						>
							VJ updated the terms for your booking.{card.askNow.hold ? " 🔒 Your next step waits until you accept them." : ""}
						</NextBox>
					)}
					{card.notes?.map((n) => (
						<div key={n.key} className={{ bad: css.badBox, good: css.good, info: css.info }[n.tone] || css.info}>
							{n.text}
						</div>
					))}
					<ParkBox parking={card.parking} />

					{body}
					<details className={css.more}>
						<summary>
							{state} unit details <span className={css.sm}>status, payment, unit</span>
						</summary>
						<UnitDetails card={card} />
					</details>
					<div className={css.help}>
						{card.crm && !waiting && card.stage !== 0 ? (
							<>
								Questions? Your CRM <b>{card.crm.name}</b>
								{card.crm.phone ? ` · ${card.crm.phone}` : ""}
							</>
						) : card.salesManager?.name ? (
							<>
								Questions? Your Sales Manager <b>{card.salesManager.name}</b>
								{card.salesManager.phone ? ` · ${card.salesManager.phone}` : ""}
							</>
						) : null}
					</div>
				</div>
				<div className={css.foot}>
					<button type="button" className={`${css.nb} ${shown === 0 ? css.nbOff : ""}`} onClick={() => shown > 0 && setView(shown - 1)}>
						← Back
					</button>
					<button type="button" className={`${css.nb} ${shown >= cur ? css.nbOff : ""}`} onClick={() => shown < cur && setView(shown + 1 === cur ? null : shown + 1)}>
						Next →
					</button>
				</div>
			</div>
			{payModals}
			{termsModal}
			{details && <AllotmentDetails open={details} handleClose={() => setDetails(false)} data={allotment} />}
			{cost && <UnitCost allotment={allotment} open={cost} handleClose={() => setCost(false)} />}
			{ModalComp && (
				<ModalComp
					allotment={allotment}
					card={card}
					mode={modal.mode}
					onClose={() => setModal(null)}
					onDone={() => {
						setModal(null);
						reload();
					}}
					reload={reload}
				/>
			)}
		</div>
	);
}
